// src/components/driver_group/DriverGroupList.js
import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Button,
  Divider,
  CardActions,
  Stack,
  Tooltip
} from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LoadingSpinner from '../common/Loadingspinner';
import ErrorAlert from '../common/ErrorAlert';

const DriverGroupList = ({ 
  driverGroups, 
  companyId, 
  isLoading, 
  isError, 
  error,
  onDeleteClick,
  getTariffName
}) => {
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading driver groups'} />;
  }

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Driver Groups</Typography>
        <Button 
          component={Link} 
          to={`/companies/${companyId}/driver_groups/new`} 
          variant="contained" 
          color="primary"
          startIcon={<AddIcon />}
        >
          Add Driver Group
        </Button>
      </Box>

      {driverGroups && driverGroups.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No driver groups found for this company
          </Typography>
          <Button 
            component={Link} 
            to={`/companies/${companyId}/driver_groups/new`} 
            variant="contained" 
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
          >
            Add First Driver Group
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {driverGroups && driverGroups.map((group) => (
            <Grid item xs={12} md={6} lg={4} key={group.DriversGroupId}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Group Name */}
                  <Typography variant="h6" component="div" gutterBottom noWrap>
                    {group.DriversGroupName}
                  </Typography>
                  
                  {/* Status Chip */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <Chip
                      label={group.DriversGroupEnabled ? "Enabled" : "Disabled"}
                      color={group.DriversGroupEnabled ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                  
                  {/* Group Details */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Group ID: {group.DriversGroupId}
                    </Typography>
                  </Box>
                  
                  {/* Tariff Information - New */}
                  {group.DriverTariffId && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <AttachMoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Tariff: {getTariffName ? getTariffName(group.DriverTariffId) : `Tariff ID: ${group.DriverTariffId}`}
                      </Typography>
                    </Box>
                  )}
                  
                  {group.DriversGroupDiscountId && (
                    <Typography variant="body2" color="text.secondary">
                      Discount ID: {group.DriversGroupDiscountId}
                    </Typography>
                  )}
                </CardContent>
                
                <Divider />
                
                {/* Action Buttons */}
                <CardActions sx={{ justifyContent: 'center', p: 2 }}>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="View Driver Group Details">
                      <Button
                        component={Link}
                        to={`/driver_groups/${group.DriversGroupId}`}
                        variant="outlined"
                        color="primary"
                        startIcon={<VisibilityIcon />}
                        size="small"
                      >
                        View
                      </Button>
                    </Tooltip>
                    
                    <Tooltip title="Edit Driver Group">
                      <Button
                        component={Link}
                        to={`/driver_groups/${group.DriversGroupId}/edit`}
                        variant="outlined"
                        color="info"
                        startIcon={<EditIcon />}
                        size="small"
                      >
                        Edit
                      </Button>
                    </Tooltip>
                    
                    <Tooltip title="Delete Driver Group">
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => onDeleteClick && onDeleteClick(group.DriversGroupId)}
                        size="small"
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </Stack>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default DriverGroupList;